import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Pointer, Camera, X, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { getEventsList, HyRaceEvent, getHyroxPortSheet, HyroxPortSheetData, updateHyroxPortSheet, UpdateHyroxPortSheetRequest, getPenaltyImage } from '../../lib/api';

interface HyroxRacesTileProps {
  // No props needed - self-contained
}

export const HyroxRacesTile: React.FC<HyroxRacesTileProps> = () => {
  const [events, setEvents] = useState<HyRaceEvent[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<HyRaceEvent | null>(null);
  const [penaltyData, setPenaltyData] = useState<HyroxPortSheetData[]>([]);
  const [isLoadingPenaltyData, setIsLoadingPenaltyData] = useState(false);
  const [showPenaltyData, setShowPenaltyData] = useState(false);
  const [modifiedRecords, setModifiedRecords] = useState<Set<number>>(new Set());
  const [pendingProcessedChanges, setPendingProcessedChanges] = useState<Map<number, boolean>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [imageRotation, setImageRotation] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [raceFilter, setRaceFilter] = useState<'current' | 'past' | 'future' | 'upcoming'>('current');
  const [highlightedPenaltyLogs, setHighlightedPenaltyLogs] = useState<Set<number>>(new Set());
  const [hideProcessed, setHideProcessed] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const [raceNumberFilter, setRaceNumberFilter] = useState<string>('all');
  const [stationFilter, setStationFilter] = useState<string>('all');
  const [judgeFilter, setJudgeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Format date to DD-mmm-YYYY (e.g., "15-Jan-2024")
  const formatDateForDisplay = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Timer effect to update button color based on time elapsed
  useEffect(() => {
    if (lastRefreshTime === null) return;

    const interval = setInterval(() => {
      setLastRefreshTime(prev => prev);
    }, 1000);

    return () => clearInterval(interval);
  }, [lastRefreshTime]);

  // Function to determine button color based on time elapsed
  const getRefreshButtonColor = () => {
    if (lastRefreshTime === null) {
      return 'bg-pink-100 text-red-800 border-pink-200 hover:bg-pink-200';
    }

    const now = Date.now();
    const timeElapsed = now - lastRefreshTime;
    const threeMinutes = 1 * 60 * 1000;
    const fiveMinutes = 2 * 60 * 1000;

    if (timeElapsed >= fiveMinutes) {
      return 'bg-pink-100 text-red-800 border-pink-200 hover:bg-pink-200';
    } else if (timeElapsed >= threeMinutes) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200';
    } else {
      return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200';
    }
  };

  // Image modal handlers
  const handleImageClick = async (penlogid: number) => {
    setIsLoadingImage(true);
    setShowImageModal(true);
    
    try {
      const result = await getPenaltyImage(penlogid);
      if (result.success && result.athracephotourl) {
        setSelectedImage(result.athracephotourl);
      } else {
        console.error('Failed to fetch penalty image:', result.error);
        setSelectedImage(null);
      }
    } catch (error) {
      console.error('Error fetching penalty image:', error);
      setSelectedImage(null);
    } finally {
      setIsLoadingImage(false);
    }
  };

  const handleRotateImage = () => {
    setImageRotation(prev => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 1.5));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setShowImageModal(false);
    setIsLoadingImage(false);
    setImageRotation(0);
    setImageZoom(1);
  };

  // Filter events based on selected filter
  const filteredEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const threeDaysFromNow = new Date(today);
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

    return events.filter(event => {
      if (!event.eventdate) {
        return raceFilter === 'past';
      }

      const eventDate = new Date(event.eventdate);
      eventDate.setHours(0, 0, 0, 0);

      if (raceFilter === 'current') {
        return eventDate.getTime() === today.getTime();
      } else if (raceFilter === 'past') {
        return eventDate.getTime() < today.getTime();
      } else if (raceFilter === 'future') {
        return eventDate.getTime() > threeDaysFromNow.getTime();
      } else if (raceFilter === 'upcoming') {
        return eventDate.getTime() >= today.getTime() && eventDate.getTime() <= threeDaysFromNow.getTime();
      }
      return true;
    });
  }, [events, raceFilter]);

  // Get unique values for filter dropdowns
  const uniqueRaceNumbers = useMemo(() => {
    const races = Array.from(new Set(penaltyData.map(p => p.racenumber).filter(Boolean))).sort();
    return races;
  }, [penaltyData]);

  const uniqueStations = useMemo(() => {
    const stations = Array.from(new Set(penaltyData.map(p => p.station).filter(Boolean))).sort();
    return stations;
  }, [penaltyData]);

  const uniqueJudges = useMemo(() => {
    const judges = Array.from(new Set(penaltyData.map(p => p.judgename).filter(Boolean))).sort();
    return judges;
  }, [penaltyData]);

  const uniqueStatuses = useMemo(() => {
    const statuses = Array.from(new Set(penaltyData.map(p => p.status).filter(Boolean))).sort();
    return statuses;
  }, [penaltyData]);

  // Filter penalty data based on hideProcessed toggle and filter dropdowns
  const filteredPenaltyData = useMemo(() => {
    return penaltyData.filter(penalty => {
      if (hideProcessed && penalty.timingprocessed) return false;
      if (raceNumberFilter !== 'all' && penalty.racenumber !== raceNumberFilter) return false;
      if (stationFilter !== 'all' && penalty.station !== stationFilter) return false;
      if (judgeFilter !== 'all' && penalty.judgename !== judgeFilter) return false;
      if (statusFilter !== 'all' && penalty.status !== statusFilter) return false;
      return true;
    });
  }, [penaltyData, hideProcessed, raceNumberFilter, stationFilter, judgeFilter, statusFilter]);

  // Paginated penalty data
  const paginatedPenaltyData = useMemo(() => {
    const sorted = [...filteredPenaltyData].sort((a, b) => 
      new Date(b.approved_at || 0).getTime() - new Date(a.approved_at || 0).getTime()
    );
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  }, [filteredPenaltyData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredPenaltyData.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [hideProcessed, raceNumberFilter, stationFilter, judgeFilter, statusFilter]);

  // Update selected event when filter changes
  useEffect(() => {
    if (filteredEvents.length > 0) {
      if (!selectedEvent || !filteredEvents.find(e => e.eventid === selectedEvent.eventid)) {
        setSelectedEvent(filteredEvents[0]);
      }
    } else {
      setSelectedEvent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceFilter, filteredEvents]);

  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoadingEvents(true);
      try {
        const result = await getEventsList();
        if (result.success) {
          setEvents(result.events);
        } else {
          console.error('Failed to fetch events:', result.error);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchEvents();
  }, []);

  // Detect portrait orientation
  useEffect(() => {
    const checkOrientation = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);

    const mediaQuery = window.matchMedia('(orientation: portrait)');
    const handleOrientationChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsPortrait(e.matches);
    };

    handleOrientationChange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleOrientationChange);
    } else {
      mediaQuery.addListener(handleOrientationChange);
    }

    return () => {
      window.removeEventListener('resize', checkOrientation);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleOrientationChange);
      } else {
        mediaQuery.removeListener(handleOrientationChange);
      }
    };
  }, []);

  // Function to post processed records to judge-records endpoint
  const postProcessedRecordsToJudgeRecords = async (records: Map<number, HyroxPortSheetData | (HyroxPortSheetData & { approved_at: null })>) => {
    if (records.size === 0) {
      return { success: true, posted: 0 };
    }

    const postedRecords: any[] = [];
    let errorCount = 0;

    for (const [, record] of records.entries()) {
      try {
        const judgeNamePart = record.judgename?.split('@')[0] || '';
        const recordId = `${record.racenumber || ""}_${judgeNamePart}_${record.penaltytype || ""}_${record.penlogid || ""}`;
        
        let stationId = "overall";
        if (record.station) {
          const firstChar = record.station.charAt(0);
          if (!isNaN(Number(firstChar)) && firstChar !== '') {
            stationId = `wo_${firstChar}`;
          } else if (record.station === "Roxzone") {
            stationId = "rz";
          }
        }
        
        const formatPenaltyTimeToISO8601 = (seconds: number | null): string => {
          if (seconds == null) return "PT0S";
          
          const isNegative = seconds < 0;
          const absSeconds = Math.abs(seconds);
          
          const hours = Math.floor(absSeconds / 3600);
          const minutes = Math.floor((absSeconds % 3600) / 60);
          const secs = absSeconds % 60;
          
          let duration = "PT";
          if (hours > 0) duration += `${hours}H`;
          if (minutes > 0) duration += `${minutes}M`;
          if (secs > 0 || duration === "PT") duration += `${secs}S`;
          
          return isNegative ? `-${duration}` : duration;
        };
        
        const payload = {
          internal_ref_key: "019a0d36-770a-7df4-857c-4e1be1f4cac8",
          record_id: recordId,
          record_source: record.record_source || "",
          event_key_code: record.event_key_code || "",
          triggered_by: record.triggered_by || "",
          created_at: record.createdate || "",
          approved_at: (record.status === "Approved" || record.status === "Needs Review") 
            ? (record.approved_at ? new Date(record.approved_at).toISOString() : null)
            : null,
          received_at: new Date().toISOString(),
          race_number: (record.racenumber && !isNaN(Number(record.racenumber))) ? record.racenumber : "",
          chip_id: (record.racenumber && record.racenumber.length === 7 && /^[A-Za-z]{5}\d{2}$/.test(record.racenumber)) ? record.racenumber : "",
          status: record.race_status || "",
          station_id: stationId,
          wo_adjust: formatPenaltyTimeToISO8601(record.penaltytime),
          wo_type: (record.comments && record.comments.trim() !== "") ? record.comments : (record.penaltytype || ""),
          comment_timing: record.timingcomment || "" 
        };

        const response = await fetch('https://ts.hyrox.dev/judge-records', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          console.error(`Failed to post record ${record.penlogid} to judge-records:`, response.statusText);
          errorCount++;
        } else {
          postedRecords.push(record.penlogid);
        }
      } catch (error) {
        console.error(`Error posting record ${record.penlogid} to judge-records:`, error);
        errorCount++;
      }
    }

    return {
      success: errorCount === 0,
      posted: postedRecords.length,
      errors: errorCount
    };
  };

  // Flag duplicate Warning statuses within 90 minutes for same Race # and Station as Needs Review
  const autoApproveDuplicateWarnings = (data: HyroxPortSheetData[]): { updatedData: HyroxPortSheetData[], modifiedIds: Set<number> } => {
    const updatedData = [...data];
    const modifiedIds = new Set<number>();
    const ninetyMinutesInMs = 90 * 60 * 1000;
    
    const penaltiesByRaceAndStation = new Map<string, HyroxPortSheetData[]>();
    
    updatedData.forEach(penalty => {
      if (penalty.status === 'Warning') {
        const key = `${penalty.racenumber}-${penalty.station}`;
        if (!penaltiesByRaceAndStation.has(key)) {
          penaltiesByRaceAndStation.set(key, []);
        }
        penaltiesByRaceAndStation.get(key)!.push(penalty);
      }
    });
    
    penaltiesByRaceAndStation.forEach((penalties) => {
      if (penalties.length < 2) return;
      
      const sortedPenalties = [...penalties].sort((a, b) => 
        new Date(a.approved_at).getTime() - new Date(b.approved_at).getTime()
      );
      
      for (let i = 1; i < sortedPenalties.length; i++) {
        const currentPenalty = sortedPenalties[i];
        const currentTime = new Date(currentPenalty.approved_at).getTime();
        
        for (let j = 0; j < i; j++) {
          const previousPenalty = sortedPenalties[j];
          const previousTime = new Date(previousPenalty.approved_at).getTime();
          const timeDifference = currentTime - previousTime;
          
          if (timeDifference <= ninetyMinutesInMs && previousPenalty.status === 'Warning') {
            const index = updatedData.findIndex(p => p.penlogid === currentPenalty.penlogid);
            if (index !== -1) {
              updatedData[index] = { ...updatedData[index], status: 'Needs Review' };
              modifiedIds.add(currentPenalty.penlogid);
            }
            break;
          }
        }
      }
    });
    
    return { updatedData, modifiedIds };
  };

  // Function to identify penalty logs where race number repeats after 90-minute window
  const analyzePenaltyDataForRepeats = (data: HyroxPortSheetData[]) => {
    const highlightedSet = new Set<number>();
    const ninetyMinutesInMs = 90 * 60 * 1000;
    
    const filteredData = data.filter(penalty => penalty.racenumber !== "Photo Captured");
    
    const penaltiesByRaceNumber = new Map<string, HyroxPortSheetData[]>();
    
    filteredData.forEach(penalty => {
      const raceNum = penalty.racenumber;
      if (!penaltiesByRaceNumber.has(raceNum)) {
        penaltiesByRaceNumber.set(raceNum, []);
      }
      penaltiesByRaceNumber.get(raceNum)!.push(penalty);
    });
    
    penaltiesByRaceNumber.forEach((penalties) => {
      const sortedPenalties = [...penalties].sort((a, b) => 
        new Date(a.approved_at).getTime() - new Date(b.approved_at).getTime()
      );
      
      for (let i = 1; i < sortedPenalties.length; i++) {
        const currentPenalty = sortedPenalties[i];
        const currentTime = new Date(currentPenalty.approved_at).getTime();
        
        for (let j = 0; j < i; j++) {
          const previousPenalty = sortedPenalties[j];
          const previousTime = new Date(previousPenalty.approved_at).getTime();
          const timeDifference = currentTime - previousTime;
          
          if (timeDifference > ninetyMinutesInMs) {
            highlightedSet.add(currentPenalty.penlogid);
            break;
          }
        }
      }
    });
    
    return highlightedSet;
  };

  const handleReviewPenaltyData = async () => {
    if (!selectedEvent) {
      alert('Please select an event first');
      return;
    }

    setIsLoadingPenaltyData(true);
    try {
      const result = await getHyroxPortSheet(selectedEvent.eventid);
      if (result.success) {
        const { updatedData, modifiedIds } = autoApproveDuplicateWarnings(result.penaltyData);
        
        const dataWithPreservedProcessed = updatedData.map(record => {
          const pendingChange = pendingProcessedChanges.get(record.penlogid);
          if (pendingChange !== undefined) {
            return { ...record, timingprocessed: pendingChange };
          }
          return record;
        });
        
        setPenaltyData(dataWithPreservedProcessed);
        setModifiedRecords(modifiedIds);
        const highlighted = analyzePenaltyDataForRepeats(dataWithPreservedProcessed);
        setHighlightedPenaltyLogs(highlighted);
        setShowPenaltyData(true);
      } else {
        console.error('Failed to fetch penalty data:', result.error);
        alert('Failed to fetch penalty data. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching penalty data:', error);
      alert('Error fetching penalty data. Please try again.');
    } finally {
      setIsLoadingPenaltyData(false);
    }
  };

  const handleDataRefresh = async () => {
    if (!selectedEvent) {
      alert('No event selected');
      return;
    }

    if (modifiedRecords.size > 0) {
      setIsSaving(true);
      let successCount = 0;
      let errorCount = 0;

      try {
        for (const penlogid of modifiedRecords) {
          const record = penaltyData.find(p => p.penlogid === penlogid);
          if (!record) continue;

          const updateData: UpdateHyroxPortSheetRequest = {
            penlogid: penlogid,
            status: record.status,
            timingcomment: record.timingcomment || undefined
          };

          const result = await updateHyroxPortSheet(updateData);
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to update record ${penlogid}:`, result.error);
          }
        }

        if (errorCount > 0) {
          alert(`Saved ${successCount} record(s), but ${errorCount} failed. Proceeding with refresh...`);
        }
      } catch (error) {
        console.error('Error saving changes before refresh:', error);
        alert('Error saving changes before refresh. Proceeding with refresh...');
      } finally {
        setIsSaving(false);
      }
    }

    setIsLoadingPenaltyData(true);
    try {
      const result = await getHyroxPortSheet(selectedEvent.eventid);
      if (result.success) {
        const { updatedData, modifiedIds } = autoApproveDuplicateWarnings(result.penaltyData);
        
        const dataWithPreservedProcessed = updatedData.map(record => {
          const pendingChange = pendingProcessedChanges.get(record.penlogid);
          if (pendingChange !== undefined) {
            return { ...record, timingprocessed: pendingChange };
          }
          return record;
        });
        
        setPenaltyData(dataWithPreservedProcessed);
        setModifiedRecords(modifiedIds);
        const highlighted = analyzePenaltyDataForRepeats(dataWithPreservedProcessed);
        setHighlightedPenaltyLogs(highlighted);
        setLastRefreshTime(Date.now());
      } else {
        console.error('Failed to refresh penalty data:', result.error);
        alert('Failed to refresh penalty data. Please try again.');
      }
    } catch (error) {
      console.error('Error refreshing penalty data:', error);
      alert('Error refreshing penalty data. Please try again.');
    } finally {
      setIsLoadingPenaltyData(false);
    }
  };

  // Auto-refresh function
  const autoRefreshPenaltyData = useCallback(async () => {
    if (!selectedEvent || !showPenaltyData) {
      return;
    }

    if (modifiedRecords.size > 0) {
      let successCount = 0;
      let errorCount = 0;

      try {
        for (const penlogid of modifiedRecords) {
          const record = penaltyData.find(p => p.penlogid === penlogid);
          if (!record) continue;

          const updateData: UpdateHyroxPortSheetRequest = {
            penlogid: penlogid,
            status: record.status,
            timingcomment: record.timingcomment || undefined
          };

          const result = await updateHyroxPortSheet(updateData);
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to update record ${penlogid} during auto-refresh:`, result.error);
          }
        }

        if (errorCount > 0) {
          console.warn(`Auto-refresh: Saved ${successCount} record(s), but ${errorCount} failed. Proceeding with refresh...`);
        } else if (successCount > 0) {
          console.log(`Auto-refresh: Successfully saved ${successCount} record(s) before refresh.`);
        }
      } catch (error) {
        console.error('Error saving changes before auto-refresh:', error);
      }
    }

    try {
      const result = await getHyroxPortSheet(selectedEvent.eventid);
      if (result.success) {
        const { updatedData, modifiedIds } = autoApproveDuplicateWarnings(result.penaltyData);
        
        const dataWithPreservedProcessed = updatedData.map(record => {
          const pendingChange = pendingProcessedChanges.get(record.penlogid);
          if (pendingChange !== undefined) {
            return { ...record, timingprocessed: pendingChange };
          }
          return record;
        });
        
        setPenaltyData(dataWithPreservedProcessed);
        setModifiedRecords(prev => {
          const merged = new Set(prev);
          modifiedIds.forEach(id => merged.add(id));
          return merged;
        });
        const highlighted = analyzePenaltyDataForRepeats(dataWithPreservedProcessed);
        setHighlightedPenaltyLogs(highlighted);
        setLastRefreshTime(Date.now());
      } else {
        console.error('Failed to auto-refresh penalty data:', result.error);
      }
    } catch (error) {
      console.error('Error auto-refreshing penalty data:', error);
    }
  }, [selectedEvent, showPenaltyData, modifiedRecords, penaltyData, pendingProcessedChanges]);

  // Auto-refresh penalty data every 30 seconds when penalty data is shown
  useEffect(() => {
    if (!showPenaltyData || !selectedEvent) {
      return;
    }

    const interval = setInterval(() => {
      autoRefreshPenaltyData();
    }, 30000);

    return () => clearInterval(interval);
  }, [showPenaltyData, selectedEvent, autoRefreshPenaltyData]);

  const handleStatusChange = (penlogid: number, newStatus: string) => {
    const updatedData = penaltyData.map(penalty => 
      penalty.penlogid === penlogid 
        ? { ...penalty, status: newStatus }
        : penalty
    );
    setPenaltyData(updatedData);
    setModifiedRecords(prev => new Set([...prev, penlogid]));
  };

  const handleProcessedChange = async (penlogid: number, newProcessed: boolean) => {
    const currentPenalty = penaltyData.find(p => p.penlogid === penlogid);
    
    const updatedData = penaltyData.map(penalty => 
      penalty.penlogid === penlogid 
        ? { ...penalty, timingprocessed: newProcessed }
        : penalty
    );
    setPenaltyData(updatedData);
    
    setPendingProcessedChanges(prev => {
      const updated = new Map(prev);
      updated.set(penlogid, newProcessed);
      return updated;
    });
    
    try {
      const updateData: UpdateHyroxPortSheetRequest = {
        penlogid: penlogid,
        timingprocessed: newProcessed
      };

      const result = await updateHyroxPortSheet(updateData);
      if (result.success) {
        setPendingProcessedChanges(prev => {
          const updated = new Map(prev);
          updated.delete(penlogid);
          return updated;
        });
        
        const updatedRecord = updatedData.find(p => p.penlogid === penlogid);
        if (updatedRecord && currentPenalty) {
          const recordToPost = !newProcessed && currentPenalty.timingprocessed
            ? { ...updatedRecord, approved_at: null as any }
            : updatedRecord;
          
          const recordsToPost = new Map([[penlogid, recordToPost as HyroxPortSheetData]]);
          const postResult = await postProcessedRecordsToJudgeRecords(recordsToPost);
          if (postResult.success) {
            console.log(`Successfully posted record ${penlogid} to judge-records endpoint (${newProcessed ? 'Yes' : 'No'})`);
          } else {
            console.warn(`Failed to post record ${penlogid} to judge-records:`, postResult.errors);
          }
        } else {
          console.warn(`Cannot post record ${penlogid} to judge-records: updatedRecord or currentPenalty not found`);
        }
      } else {
        console.error(`Failed to save timingprocessed for record ${penlogid}:`, result.error);
      }
    } catch (error) {
      console.error(`Error saving timingprocessed for record ${penlogid}:`, error);
    }
  };

  const handleTimingCommentChange = (penlogid: number, newComment: string) => {
    const updatedData = penaltyData.map(penalty => 
      penalty.penlogid === penlogid 
        ? { ...penalty, timingcomment: newComment }
        : penalty
    );
    setPenaltyData(updatedData);
    setModifiedRecords(prev => new Set([...prev, penlogid]));
  };

  const handleSaveChanges = async () => {
    if (modifiedRecords.size === 0) {
      alert('No changes to save');
      return;
    }

    setIsSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const penlogid of modifiedRecords) {
        const record = penaltyData.find(p => p.penlogid === penlogid);
        if (!record) continue;

        const updateData: UpdateHyroxPortSheetRequest = {
          penlogid: penlogid,
          status: record.status,
          timingcomment: record.timingcomment || undefined
        };

        const result = await updateHyroxPortSheet(updateData);
        if (result.success) {
          successCount++;
          
          if (result.data && result.data.timingprocessed !== undefined) {
            setPenaltyData(prevData => 
              prevData.map(p => 
                p.penlogid === penlogid 
                  ? { ...p, timingprocessed: result.data!.timingprocessed }
                  : p
              )
            );
          }
        } else {
          errorCount++;
          console.error(`Failed to update record ${penlogid}:`, result.error);
        }
      }

      if (errorCount === 0) {
        alert(`Successfully saved ${successCount} record(s)`);
        setModifiedRecords(new Set());
      } else {
        alert(`Saved ${successCount} record(s), but ${errorCount} failed. Check console for details.`);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredPenaltyData.length === 0) return null;
    
    const pageNumbers = [];
    const maxVisiblePages = 7;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return (
      <div className="flex items-center justify-between mb-4 px-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredPenaltyData.length)} of {filteredPenaltyData.length} records
          </span>
          <Select value={itemsPerPage.toString()} onValueChange={(value) => {
            setItemsPerPage(Number(value));
            setCurrentPage(1);
          }}>
            <SelectTrigger className="h-8 w-20 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-700">per page</span>
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            variant="outline"
            className="h-8 px-2 text-xs disabled:opacity-50"
          >
            First
          </Button>
          <Button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            variant="outline"
            className="h-8 px-2 text-xs disabled:opacity-50"
          >
            Prev
          </Button>
          
          {startPage > 1 && (
            <>
              <Button
                onClick={() => setCurrentPage(1)}
                variant="outline"
                className="h-8 w-8 text-xs"
              >
                1
              </Button>
              {startPage > 2 && <span className="px-1 text-gray-500">...</span>}
            </>
          )}
          
          {pageNumbers.map(page => (
            <Button
              key={page}
              onClick={() => setCurrentPage(page)}
              variant={currentPage === page ? "default" : "outline"}
              className={`h-8 w-8 text-xs ${currentPage === page ? 'bg-black text-white' : ''}`}
            >
              {page}
            </Button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-1 text-gray-500">...</span>}
              <Button
                onClick={() => setCurrentPage(totalPages)}
                variant="outline"
                className="h-8 w-8 text-xs"
              >
                {totalPages}
              </Button>
            </>
          )}
          
          <Button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            variant="outline"
            className="h-8 px-2 text-xs disabled:opacity-50"
          >
            Next
          </Button>
          <Button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            variant="outline"
            className="h-8 px-2 text-xs disabled:opacity-50"
          >
            Last
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {!showPenaltyData && (
        <Card>
          <CardContent className="p-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">HYROX Races</h2>
            
            {/* Filter Cards */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Select a Race</h3>
              <div className="flex gap-4 mb-4">
                <Card
                  className={`cursor-pointer transition-all duration-200 flex-1 ${
                    raceFilter === 'current'
                      ? 'ring-2 ring-black shadow-lg bg-gray-100'
                      : 'hover:shadow-md bg-white'
                  }`}
                  onClick={() => setRaceFilter('current')}
                >
                  <CardContent className="p-4">
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 mb-1">Current Races</h4>
                      <p className="text-sm text-gray-600">
                        {events.filter(e => {
                          if (!e.eventdate) return false;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const eventDate = new Date(e.eventdate);
                          eventDate.setHours(0, 0, 0, 0);
                          return eventDate.getTime() === today.getTime();
                        }).length} events
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className={`cursor-pointer transition-all duration-200 flex-1 ${
                    raceFilter === 'upcoming'
                      ? 'ring-2 ring-black shadow-lg bg-gray-100'
                      : 'hover:shadow-md bg-white'
                  }`}
                  onClick={() => setRaceFilter('upcoming')}
                >
                  <CardContent className="p-4">
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 mb-1">Upcoming Races</h4>
                      <p className="text-sm text-gray-600">
                        {events.filter(e => {
                          if (!e.eventdate) return false;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const threeDaysFromNow = new Date(today);
                          threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
                          const eventDate = new Date(e.eventdate);
                          eventDate.setHours(0, 0, 0, 0);
                          return eventDate.getTime() >= today.getTime() && eventDate.getTime() <= threeDaysFromNow.getTime();
                        }).length} events
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className={`cursor-pointer transition-all duration-200 flex-1 ${
                    raceFilter === 'future'
                      ? 'ring-2 ring-black shadow-lg bg-gray-100'
                      : 'hover:shadow-md bg-white'
                  }`}
                  onClick={() => setRaceFilter('future')}
                >
                  <CardContent className="p-4">
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 mb-1">Future Races</h4>
                      <p className="text-sm text-gray-600">
                        {events.filter(e => {
                          if (!e.eventdate) return false;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const threeDaysFromNow = new Date(today);
                          threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
                          const eventDate = new Date(e.eventdate);
                          eventDate.setHours(0, 0, 0, 0);
                          return eventDate.getTime() > threeDaysFromNow.getTime();
                        }).length} events
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className={`cursor-pointer transition-all duration-200 flex-1 ${
                    raceFilter === 'past'
                      ? 'ring-2 ring-black shadow-lg bg-gray-100'
                      : 'hover:shadow-md bg-white'
                  }`}
                  onClick={() => setRaceFilter('past')}
                >
                  <CardContent className="p-4">
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 mb-1">Past Races</h4>
                      <p className="text-sm text-gray-600">
                        {events.filter(e => {
                          if (!e.eventdate) return true;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const eventDate = new Date(e.eventdate);
                          eventDate.setHours(0, 0, 0, 0);
                          return eventDate.getTime() < today.getTime();
                        }).length} events
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Events List */}
            <div className="mb-4">
              {isLoadingEvents ? (
                <div className="text-center py-4">
                  <div className="text-gray-500">Loading events...</div>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="text-center py-4">
                  <div className="text-gray-500">No {raceFilter} events found</div>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredEvents.map((event) => (
                    <Button
                      key={event.eventid}
                      variant={selectedEvent?.eventid === event.eventid ? "default" : "outline"}
                      className={`w-1/5 justify-start text-left h-auto p-3 group ${
                        selectedEvent?.eventid === event.eventid 
                          ? 'bg-gray-700 text-white' 
                          : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-green-800'
                      }`}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="flex flex-col items-start">
                        <div className="font-medium">{event.eventname}</div>
                        <div className={`text-xs ${
                          selectedEvent?.eventid === event.eventid ? 'text-gray-300' : 'text-gray-500 group-hover:text-green-600'
                        }`}>
                          {formatDateForDisplay(event.eventstarttime)}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Event Details */}
            {selectedEvent && (
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Selected Race Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Event Name</div>
                    <div className="font-medium">{selectedEvent.eventname}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Location</div>
                    <div className="font-medium">{selectedEvent.eventlocation}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Event Date</div>
                    <div className="font-medium">
                      {formatDateForDisplay(selectedEvent.eventstarttime)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Status</div>
                    <div className="font-medium capitalize">{selectedEvent.eventstatus}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Load Penalty Data Button */}
            {selectedEvent && (
              <div className="mb-4">
                <Button
                  onClick={handleReviewPenaltyData}
                  disabled={isLoadingPenaltyData}
                  className="w-full bg-gray-300 hover:bg-gray-800 text-blue-500 py-3"
                >
                  {isLoadingPenaltyData ? 'Loading...' : (
                    <>
                      <Pointer className="w-4 h-4 mr-2" />
                      Load Penalty Timing Data
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Penalty Data Grid */}
      {showPenaltyData && (
        <Card className="w-full">
          <CardContent className="pt-4 px-4 pb-2">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Penalty Data</h2>
                <p className="text-gray-600 mt-1">{selectedEvent?.eventname} - {filteredPenaltyData.length} {hideProcessed ? `unprocessed (${penaltyData.length} total)` : 'records'}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setHideProcessed(!hideProcessed)}
                  variant="outline"
                  className={hideProcessed ? "bg-orange-100 text-orange-800 hover:bg-orange-200" : "bg-blue-100 text-blue-800 hover:bg-blue-200"}
                >
                  {hideProcessed ? 'Show All' : 'Hide Processed'}
                </Button>
                <Button
                  onClick={handleDataRefresh}
                  disabled={isLoadingPenaltyData}
                  variant="outline"
                  className={`${getRefreshButtonColor()} disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {isLoadingPenaltyData ? 'Refreshing...' : 'Pull Penalties'}
                </Button>
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving || modifiedRecords.size === 0}
                  className="bg-black hover:bg-gray-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : `Save Changes (${modifiedRecords.size})`}
                </Button>
                <Button
                  onClick={() => setShowPenaltyData(false)}
                  variant="outline"
                  className="text-gray-600 hover:text-gray-800"
                >
                  ← Back to Races
                </Button>
              </div>
            </div>
            
            {/* Pagination Controls */}
            {renderPagination()}
            
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[800px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs">Race #</span>
                          <Select value={raceNumberFilter} onValueChange={setRaceNumberFilter}>
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              {uniqueRaceNumbers.map(race => (
                                <SelectItem key={race} value={race}>{race}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs">Station</span>
                          <Select value={stationFilter} onValueChange={setStationFilter}>
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              {uniqueStations.map(station => (
                                <SelectItem key={station} value={station}>{station}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </th>
                      {!isPortrait && <th className="px-4 py-3 text-left font-medium text-gray-700">Penalty Type</th>}
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Time (s)</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-700">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs">Judge</span>
                          <Select value={judgeFilter} onValueChange={setJudgeFilter}>
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              {uniqueJudges.map(judge => (
                                <SelectItem key={judge} value={judge}>{judge}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </th>
                      {!isPortrait && (
                        <th className="px-2 py-2 text-left font-medium text-gray-700">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs">Race Director Status</span>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                              <SelectTrigger className="h-7 text-xs w-full">
                                <SelectValue placeholder="All" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                {uniqueStatuses.map(status => (
                                  <SelectItem key={status} value={status}>{status}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </th>
                      )}
                      {!isPortrait && <th className="px-4 py-3 text-left font-medium text-gray-700">Notes Submitted</th>}
                      {!isPortrait && <th className="px-4 py-3 text-left font-medium text-gray-700">Timing Comment</th>}
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Created</th>
                      <th className="px-2 py-3 text-left font-medium text-gray-700">Timing Processed</th>
                      <th className="px-2 py-3 text-left font-medium text-gray-700">Photo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedPenaltyData.map((penalty) => (
                      <tr key={penalty.penlogid} className={`hover:bg-gray-50 ${
                        modifiedRecords.has(penalty.penlogid) ? 'bg-gray-50 border-l-4 border-l-black' : 
                        highlightedPenaltyLogs.has(penalty.penlogid) ? 'bg-blue-100 border-l-4 border-l-blue-500' : 
                        ''
                      }`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{penalty.racenumber}</td>
                        <td className="px-4 py-3 text-gray-700">{penalty.station}</td>
                        {!isPortrait && <td className="px-4 py-3 text-gray-700">{penalty.penaltytype}</td>}
                        <td className="px-4 py-3 text-gray-700">{penalty.penaltytime}</td>
                        <td className="px-2 py-3 text-gray-700">{penalty.judgename}</td>
                        {!isPortrait && (
                          <td className="px-2 py-3 text-gray-700">
                            <Select
                              value={penalty.status}
                              onValueChange={(value) => handleStatusChange(penalty.penlogid, value)}
                            >
                              <SelectTrigger className={`w-32 h-8 text-xs ${
                                penalty.status === 'Approved' 
                                  ? 'text-green-800 bg-green-100 border-green-200' 
                                  : penalty.status === 'Needs Review'
                                  ? 'text-yellow-800 bg-yellow-100 border-yellow-200'
                                  : penalty.status === 'Warning'
                                  ? 'text-orange-800 bg-orange-100 border-orange-200'
                                  : penalty.status === 'Rejected'
                                  ? 'text-red-800 bg-pink-100 border-pink-200'
                                  : 'text-gray-800 bg-gray-100 border-gray-200'
                              }`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Approved" className="text-green-800 bg-green-100 hover:bg-green-200 focus:bg-green-200">
                                  Approved
                                </SelectItem>
                                <SelectItem value="Needs Review" className="text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:bg-yellow-200">
                                  Needs Review
                                </SelectItem>
                                <SelectItem value="Warning" className="text-orange-800 bg-orange-100 hover:bg-orange-200 focus:bg-orange-200">
                                  Warning
                                </SelectItem>
                                <SelectItem value="Rejected" className="text-red-800 bg-pink-100 hover:bg-pink-200 focus:bg-pink-200">
                                  Rejected
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        )}
                        {!isPortrait && (
                          <td className="px-4 py-3 text-gray-700 max-w-20" title={penalty.comments || ''}>
                            {penalty.comments || '-'}
                          </td>
                        )}
                        {!isPortrait && (
                          <td className="px-4 py-3 text-gray-700">
                            <Input
                              value={penalty.timingcomment || ''}
                              onChange={(e) => handleTimingCommentChange(penalty.penlogid, e.target.value)}
                              placeholder="Optional comment..."
                              className="w-36 h-8 text-xs placeholder:italic"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 text-gray-700 text-xs">
                          {new Date(penalty.approved_at).toLocaleString(undefined, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false
                          })}
                        </td>
                        <td className="px-2 py-3 text-gray-700">
                          <Select
                            value={penalty.timingprocessed ? 'Yes' : 'No'}
                            onValueChange={(value) => handleProcessedChange(penalty.penlogid, value === 'Yes')}
                          >
                            <SelectTrigger className={`w-20 h-8 text-xs ${
                              penalty.timingprocessed 
                                ? 'text-green-800 bg-green-100 border-green-200' 
                                : 'text-red-800 bg-red-100 border-red-200'
                            }`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes" className="text-green-800 bg-green-100 hover:bg-green-200 focus:bg-green-200">
                                Yes
                              </SelectItem>
                              <SelectItem value="No" className="text-red-800 bg-red-100 hover:bg-red-200 focus:bg-red-200">
                                No
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-3 text-center">
                          {penalty.hasimage ? (
                            <button
                              onClick={() => handleImageClick(penalty.penlogid)}
                              className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200 group"
                              title="View Photo"
                            >
                              <Camera className="w-4 h-4 text-blue-600 group-hover:text-blue-800" />
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {penaltyData.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No penalty data found for this event.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-6xl max-h-[95vh] w-full mx-4 relative">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Penalty Photo</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleZoomOut}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  title="Zoom Out"
                  disabled={imageZoom <= 0.5}
                >
                  <ZoomOut className={`w-5 h-5 ${imageZoom <= 0.5 ? 'text-gray-400' : 'text-gray-600'}`} />
                </button>
                <button
                  onClick={handleZoomIn}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  title="Zoom In"
                  disabled={imageZoom >= 1.5}
                >
                  <ZoomIn className={`w-5 h-5 ${imageZoom >= 1.5 ? 'text-gray-400' : 'text-gray-600'}`} />
                </button>
                <button
                  onClick={handleRotateImage}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  title="Rotate Image"
                >
                  <RotateCw className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={closeImageModal}
                  className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            
            <div className="flex justify-center">
              {isLoadingImage ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Loading image...</p>
                </div>
              ) : selectedImage ? (
                <img
                  src={selectedImage}
                  alt="Penalty Photo"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg transition-transform duration-300"
                  style={{ 
                    transform: `rotate(${imageRotation}deg) scale(${imageZoom})`,
                    transformOrigin: 'center'
                  }}
                  onError={(e) => {
                    console.error('Error loading image:', e);
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBGb3VuZDwvdGV4dD48L3N2Zz4=';
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <Camera className="w-16 h-16 text-gray-400 mb-4" />
                  <p className="text-gray-600">No image available for this penalty</p>
                </div>
              )}
            </div>
            
            <div className="mt-4 text-center">
              <button
                onClick={closeImageModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

